/*
 * Pure helpers for working hours patterns (see workingHoursApi.js for the
 * shape). DayOfWeek is 0 = Sunday ... 6 = Saturday, the same as FullCalendar
 * daysOfWeek. Dates are 'yyyy-MM-dd' strings and times 'HH:mm', so plain
 * string comparison orders them correctly.
 */

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/* Monday first, as the calendar is used */
export const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

/* A split shift at most; the server enforces the same limit */
export const MAX_BLOCKS_PER_DAY = 2;

const pad = n => String(n).padStart(2, '0');

/** Local date as 'yyyy-MM-dd' (not toISOString, which would shift to UTC) */
export const toDateString = date =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

/** The pattern covering dateStr, or null when the person is unrestricted that day */
export const patternInEffect = (patterns = [], dateStr) =>
    patterns.find(p => p.EffectiveFrom <= dateStr && (!p.EffectiveTo || dateStr <= p.EffectiveTo)) ?? null;

/** FullCalendar businessHours for a pattern: one entry per block */
export const patternToBusinessHours = pattern =>
    (pattern?.Days ?? []).map(d => ({
        daysOfWeek: [d.DayOfWeek],
        startTime: d.StartTime,
        endTime: d.FinishTime,
    }));

/**
 * businessHours for a person on dateStr, or undefined when they have no
 * pattern in effect so the resource keeps the calendar's default shading.
 */
export const businessHoursForDate = (patterns, dateStr) => {
    const pattern = patternInEffect(patterns, dateStr);
    return pattern ? patternToBusinessHours(pattern) : undefined;
};

/*
 * resource businessHours that shade nothing. FullCalendar businessHours can't
 * vary by date, so people with working hours get this and their shading comes
 * from workingHoursBackgroundEvents instead, which is worked out per date.
 */
export const ALL_DAY_BUSINESS_HOURS = [
    { daysOfWeek: [0, 1, 2, 3, 4, 5, 6], startTime: '00:00', endTime: '24:00' },
];

/** The parts of a day ('HH:mm' pairs, ending at '24:00') outside the given blocks */
export const nonWorkingIntervals = (blocks = []) => {
    const sorted = [...blocks].sort((a, b) => a.StartTime.localeCompare(b.StartTime));
    const gaps = [];
    let cursor = '00:00';
    for (const b of sorted) {
        if (b.StartTime > cursor) {
            gaps.push({ start: cursor, end: b.StartTime });
        }
        if (b.FinishTime > cursor) {
            cursor = b.FinishTime;
        }
    }
    if (cursor < '24:00') {
        gaps.push({ start: cursor, end: '24:00' });
    }
    return gaps;
};

/**
 * Background events shading the time each person is not working, one set per
 * date from fromDate up to (not including) toDate, so a change of pattern part
 * way through the view shows from the right day. On a date with no pattern in
 * effect the person is unrestricted, so the calendar's default business hours
 * (defaultHours: {daysOfWeek, startTime, endTime}) are shaded as for everyone
 * else.
 */
export const workingHoursBackgroundEvents = (parties = [], fromDate, toDate, defaultHours) => {
    const events = [];
    for (let date = new Date(fromDate); date < toDate; date = addDays(date, 1)) {
        const dateStr = toDateString(date);
        const nextDateStr = toDateString(addDays(date, 1));
        const dow = date.getDay();
        for (const party of parties) {
            const pattern = patternInEffect(party.Patterns, dateStr);
            const blocks = pattern
                ? pattern.Days.filter(d => d.DayOfWeek === dow)
                : (defaultHours.daysOfWeek.includes(dow)
                    ? [{ StartTime: defaultHours.startTime, FinishTime: defaultHours.endTime }]
                    : []);
            for (const gap of nonWorkingIntervals(blocks)) {
                events.push({
                    resourceId: String(party.PartyID),
                    start: `${dateStr}T${gap.start}`,
                    end: gap.end === '24:00' ? `${nextDateStr}T00:00` : `${dateStr}T${gap.end}`,
                    display: 'background',
                    classNames: ['working-hours__off'],
                    // the diary's own background events (availability, tasks) must still render
                    extendedProps: { isWorkingHours: true },
                });
            }
        }
    }
    return events;
};

/**
 * Client-side check matching the server's save rules, so the user gets an
 * immediate message; the server still validates. Returns an error message or
 * null.
 */
export const validateDays = days => {
    if (!days.length) {
        return 'Add at least one working block';
    }
    for (const d of days) {
        if (!d.StartTime || !d.FinishTime || d.StartTime >= d.FinishTime) {
            return `${DAY_NAMES[d.DayOfWeek]}: the start must be before the finish`;
        }
    }
    for (const dow of DAY_ORDER) {
        const blocks = days.filter(d => d.DayOfWeek === dow)
            .sort((a, b) => a.StartTime.localeCompare(b.StartTime));
        if (blocks.length > MAX_BLOCKS_PER_DAY) {
            return `${DAY_NAMES[dow]}: no more than ${MAX_BLOCKS_PER_DAY} working blocks a day`;
        }
        for (let i = 1; i < blocks.length; i++) {
            if (blocks[i].StartTime < blocks[i - 1].FinishTime) {
                return `${DAY_NAMES[dow]}: working blocks must not overlap`;
            }
        }
    }
    return null;
};

/** Short text for one pattern's week, e.g. "Mon 09:00–17:00; Tue 09:00–12:00, 13:00–17:00" */
export const describeDays = (days = []) => {
    const parts = DAY_ORDER
        .map(dow => {
            const blocks = days.filter(d => d.DayOfWeek === dow)
                .sort((a, b) => a.StartTime.localeCompare(b.StartTime));
            return blocks.length
                ? `${DAY_NAMES[dow].slice(0, 3)} ${blocks.map(b => `${b.StartTime}–${b.FinishTime}`).join(', ')}`
                : null;
        })
        .filter(Boolean);
    return parts.length ? parts.join('; ') : 'No working days';
};
